import { FormHandles, useField } from '@unform/core';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { StylesConfig, Props } from 'react-select';
import { CSSProperties } from 'styled-components';

import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import {
  SelectComponent,
  ContainerInput,
  ContainerError,
  CONTROL_HEIGHT,
} from './styles';

export interface optionProps {
  value: string | number;
  label: string;
  object?: any;
}

interface inputProps extends Props {
  name: string;
  options?: optionProps[];
  label?: string;
  isFlex1Mobile?: boolean;
  heightMobile?: number;
  clearValueChange?: boolean;
  refImperative?: any;
}

const Select: React.FC<inputProps> = ({
  name,
  options,
  label,
  children,
  isFlex1Mobile = false,
  heightMobile,
  clearValueChange = false,
  refImperative,
  ...rest
}) => {
  const selectRef = useRef<any>(null);
  const [filled, setFilled] = useState(false);
  const customControlStyles: CSSProperties = {
    color: colors.onSurface,
    outline: 'none',
    borderRadius: 8,
    boxShadow: 'none',
    backgroundColor: colors.surfaceContainerHigh,

    border: 0,
  };

  let propsMobile;

  const selectStyle: StylesConfig = {
    container: provided => ({
      ...provided,
      width: '100%',
    }),

    control: (provided, state) => ({
      ...provided,
      ...customControlStyles,
      borderColor: state.isFocused ? colors.primary : 'transparent',

      width: '100%',
      minHeight: CONTROL_HEIGHT,

      '&:focus': {
        borderColor: colors.primary,
      },
      '&:active': {
        borderColor: colors.primary,
      },

      '@media (max-width: 1199px)': {
        minHeight: heightMobile || 44,
        maxHeight: heightMobile || 44,

        fontSize: 18,
      },
    }),

    valueContainer: provided => ({
      ...provided,
      padding: '2px 16px',
    }),

    placeholder: provided => ({
      ...provided,
      color: colors.onSurfaceVariant,
    }),

    menu: provided => ({
      ...provided,
      backgroundColor: colors.surfaceContainerHigh,
      borderRadius: 8,
      overflow: 'hidden',
    }),

    option: (provided, state) => ({
      ...provided,

      color: state.isSelected ? colors.onPrimary : colors.onSurface,
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: state.isSelected
        ? colors.primary
        : state.isFocused
        ? `${colors.primary}30`
        : undefined,

      ':active': {
        backgroundColor: state.isSelected
          ? colors.primary
          : `${colors.primary}30`,
      },
    }),

    indicatorSeparator: () => ({
      display: 'none',
    }),

    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: colors.onSurfaceVariant,

      '&:hover': {
        color: colors.onSurfaceVariant,
      },

      ...(state.isFocused && { color: colors.primary }),
    }),

    indicatorsContainer: (provided, state) => ({
      ...provided,

      '@media (max-width: 1199px)': {
        minHeight: heightMobile || 44,
        maxHeight: heightMobile || 44,

        fontSize: 18,
      },
    }),

    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = 'opacity 300ms';

      return { ...provided, color: colors.onSurface, opacity, transition };
    },

    // O menu é portalado pro <body> (ver menuPortalTarget abaixo) pra não
    // ser cortado por ancestrais com overflow:hidden/auto -- caso mais
    // comum é dentro de modais (ModalContainer usa overflow-y: auto).
    // z-index precisa ficar acima do Container do modal (1000).
    menuPortal: provided => ({ ...provided, zIndex: 1600 }),
  };

  const { defaultValue, fieldName, error, registerField } = useField(name);

  useImperativeHandle(refImperative, () => ({
    clear() {
      selectRef.current.clearValue();
    },
  }));

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      getValue: (ref: any) => {
        // Nada selecionado precisa virar '' (falsy, mesmo "vazio" de um
        // campo de texto), não []: um array chega truthy em qualquer
        // checagem e, pior, quebra schemas de validação tipados como
        // string (ex. `Joi.string().allow(null, '')` no backend) --
        // sintoma real: campo opcional (UF) rejeitado como inválido
        // exatamente quando deixado em branco, achado em 2026-09-21.
        if (rest.isMulti) {
          return ref.props.value
            ? ref.props.value.map((option: inputProps) => option.value)
            : [];
        }

        return ref.props.value ? ref.props.value.value : '';
      },
    });
  }, [fieldName, registerField, rest.isMulti]);

  const handleSetFilled = useCallback(() => {
    if (clearValueChange) {
      selectRef.current.clearValue();
    } else {
      setFilled(!!selectRef.current?.props?.value?.value || false);
    }
  }, []);

  return (
    <ContainerInput isFlex1Mobile={isFlex1Mobile}>
      {label && <span>{label}</span>}
      <SelectComponent
        isErrored={!!error}
        isFilled={filled}
        styles={selectStyle}
        menuPortalTarget={document.body}
        defaultValue={defaultValue}
        ref={selectRef}
        options={options}
        classNamePrefix="react-select"
        onMenuClose={handleSetFilled}
        onBlur={handleSetFilled}
        // onChange={handleSelect}
        {...rest}
      />
      {error && (
        <ContainerError>
          <Icon name="error" size={16} color={colors.error} />
          <span>{error}</span>
        </ContainerError>
      )}
    </ContainerInput>
  );
};

export { Select };
